import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_FIREWALLS = 'getFirewalls'
export const GET_FIREWALL = 'getFirewall'
export const MANAGE_FIREWALL = 'manageFirewall'
export const DELETE_FIREWALL = 'deleteFirewall'

// mutation types
export const SAVE_FIREWALLS = 'saveFirewalls'
export const SAVE_FIREWALL = 'saveFirewall'
export const CLEAR_FIREWALLS = 'clearFirewalls'
export const SELECT_FIREWALL = 'selectFirewall'
export const UNSELECT_FIREWALL = 'unselectFirewall'
export const SAVE_MANAGE_FIREWALL_ERRORS = 'saveManageFirewallErrors'
export const CLEAR_MANAGE_FIREWALL_ERRORS = 'clearManageFirewallErrors'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_FIREWALLS = 'showToast/SettingsFirewalls'

// returns firewall based on id
function findFirewall(state, id) {
  return state.firewallsList.filter(item => item.id === id)[0]
}

export const DEFAULT_SELECTED_FIREWALL = {
  id: undefined,
  name: '',
  slug: '',
  groups_used: 0,
  source_raw: '',
}

const state = {
  firewallsList: [],
  selectedFirewall: { ...DEFAULT_SELECTED_FIREWALL },
  selectedFirewallId: null,
  manageFirewallErrors: [],
  firewallSourceCodePlaceholder: `table ip nat {
    chain prerouting {
            type nat hook prerouting priority dstnat; policy accept;
    }
}`,

  responses: {
    firewallsList: false,
    getFirewall: false,
    manageFirewall: false,
    deleteFirewall: false,
  },
}

const getters = {
  firewallsList(state) {
    return state.firewallsList
  },
  firewallsEmpty(state) {
    return state.firewallsList.length == 0
  },
  totalFirewalls(state) {
    return state.firewallsList.length
  },
  selectedFirewall(state) {
    return state.selectedFirewall
  },
  selectedFirewallExist(state) {
    return state.firewallsList.some(firewall => firewall.id == state.selectedFirewallId)
  },
  firewallsModalTitle(state) {
    return state.selectedFirewallId ? 'Edit firewall' : 'Add new firewall'
  },
  manageFirewallErrors(state) {
    return state.manageFirewallErrors
  },
  manageFirewallError: state => field => {
    return state.manageFirewallErrors.filter(err => err.field == field)
  },
  firewallSourceCodePlaceholder(state) {
    return state.firewallSourceCodePlaceholder
  },
  firewallsResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_FIREWALLS](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'firewallsList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_FIREWALLS)
          .then(({ data }) => {
            context.commit(SAVE_FIREWALLS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_FIREWALLS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'firewallsList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_FIREWALL](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getFirewall', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_FIREWALL}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_FIREWALL, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_FIREWALLS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getFirewall', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_FIREWALL](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageFirewall', status: true })
        eventBus.$emit('startProgress')

        let method
        let url

        if (context.getters.selectedFirewallExist) {
          method = 'put'
          url = `${process.env.VUE_APP_FIREWALL}/${state.selectedFirewallId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_FIREWALL
        }

        ApiService[method](url, state.selectedFirewall)
          .then(({ data }) => {
            context.dispatch(GET_FIREWALLS)
            eventBus.$emit(TOAST_FIREWALLS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_FIREWALL_ERRORS, error.errors)
            eventBus.$emit(TOAST_FIREWALLS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageFirewall', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_FIREWALL](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteFirewall', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_FIREWALL}/${state.selectedFirewallId}`)
          .then(({ data }) => {
            context.dispatch(GET_FIREWALLS)
            eventBus.$emit(TOAST_FIREWALLS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_FIREWALLS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteFirewall', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_FIREWALLS](state, firewalls) {
    state.firewallsList = firewalls
  },
  [SAVE_FIREWALL](state, firewall) {
    const target = findFirewall(state, firewall.id)
    if (target) {
      Object.keys(firewall).forEach(key => {
        Vue.set(target, key, firewall[key])
      })
    }
  },
  [CLEAR_FIREWALLS](state) {
    state.firewallsList = []
  },
  [SELECT_FIREWALL](state, firewall) {
    Object.keys(firewall).forEach(key => {
      state.selectedFirewall[key] = firewall[key]
    })
    state.selectedFirewallId = firewall.id
  },
  [UNSELECT_FIREWALL](state) {
    state.selectedFirewall = { ...DEFAULT_SELECTED_FIREWALL }
    state.selectedFirewallId = null
  },
  [SAVE_MANAGE_FIREWALL_ERRORS](state, errors) {
    state.manageFirewallErrors = errors
  },
  [CLEAR_MANAGE_FIREWALL_ERRORS](state) {
    state.manageFirewallErrors = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
